import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceState } from '@/hooks/useWorkspaceState';
import { Search, Filter, Database, FileText, Download, MoreVertical, ChevronLeft, ChevronRight, Plus, ExternalLink, FileSpreadsheet, RefreshCw, Loader2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useSessionStore, useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { USApplicationDisplay } from './uspto/USApplicationDisplay';
import { ExportModal } from './uspto/ExportModal';
import { documentApi, patentApi } from '@/services/api';
import { addLoadingDocument, removeLoadingDocument, getLoadingDocuments } from '@/utils/loadingDocuments';

interface USPTODataPageProps {
  onDocumentSelect?: (doc: any) => void;
}

interface ApplicationResource {
  id: string;
  type: 'application';
  number: string;
  data: {
    application_number: string;
    patent_info: {
      applicationMetaData: any;
    };
    pgpubDocumentMetaData?: any;
    grantDocumentMetaData?: any;
  };
}

export function USPTODataPage({ onDocumentSelect }: USPTODataPageProps) {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useWorkspaceState('uspto:search', '');
  const [filterStatus, setFilterStatus] = useWorkspaceState('uspto:status', 'all');
  const [selectedResource, setSelectedResource] = useState<ApplicationResource | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddInput, setShowAddInput] = useState(false);
  const [newApplicationNumber, setNewApplicationNumber] = useState('');
  const [refreshingApps, setRefreshingApps] = useState<Set<string>>(new Set());
  const [refreshingAll, setRefreshingAll] = useState(false);
  // The application awaiting confirmation, and whether the delete is in flight. Deleting
  // removes it from this user's list — it is not a hard delete of the application data,
  // which is cached for everyone — but it is still a list the user curated, so it asks.
  const [applicationToDelete, setApplicationToDelete] = useState<ApplicationResource | null>(null);
  const [isDeletingApp, setIsDeletingApp] = useState(false);
  // Bulk add: a pasted list of application numbers. Separate state from the single-add
  // input because the two are different controls, and a half-typed one should survive
  // opening the other.
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [bulkApplicationNumbers, setBulkApplicationNumbers] = useState('');
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  /**
   * Applications being fetched from the USPTO right now.
   *
   * Read from the shared loading-documents store rather than kept here, so it survives
   * leaving the page and coming back — the fetch is a backend job that carries on
   * regardless, and a spinner that vanishes on navigation is worse than none.
   */
  const [pendingApplications, setPendingApplications] = useState<string[]>([]);

  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const setSessionApplications = useSessionStore(state => state.setSessionApplications);
  const setSessionDocuments = useSessionStore(state => state.setSessionDocuments);
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();

  // Handle initial loading state
  useEffect(() => {
    // If we have applications, stop loading immediately
    if (sessionApplications && sessionApplications.length > 0) {
      setIsLoading(false);
    } else {
      // Give it 2 seconds to load, then show "no data" message
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [sessionApplications]);

  /**
   * Re-read the application list, optionally waiting for one to show up.
   *
   * A single read right after a job completes is not enough. The row is written by the
   * worker, and the read goes through a response cache and an eventually-consistent
   * table, so the first read can legitimately come back without it — and nothing read
   * again, which is why an added application could sit invisible until a reload. This
   * retries on a widening delay and stops the moment the application is in the list.
   */
  const refreshSessionApplications = useCallback(
    async (waitFor?: string) => {
      const digits = (value: string) => String(value ?? '').replace(/\D/g, '');
      const target = waitFor ? digits(waitFor) : '';
      // Spread across ~30s, which comfortably outlives the backend's short response
      // cache as well as any write lag.
      const delays = [0, 500, 1000, 2000, 3000, 5000, 8000, 10000];

      for (let attempt = 0; attempt < delays.length; attempt++) {
        if (delays[attempt] > 0) {
          await new Promise(resolve => setTimeout(resolve, delays[attempt]));
        }

        try {
          const fresh = await patentApi.getSessionApplications(true);
          setSessionApplications(fresh);
          localStorage.setItem('session_applications', JSON.stringify(fresh));

          if (!target) return fresh;

          const present = (fresh || []).some((app: any) =>
            digits(app?.application_id || app?.application?.applicationNumberText) === target
          );
          if (present) return fresh;
        } catch (error) {
          console.error('Failed to refresh session applications:', error);
        }
      }

      console.warn('Application did not appear in the list after retrying:', waitFor);
      return null;
    },
    [setSessionApplications]
  );

  // The applications currently being fetched, kept in step with the shared store.
  useEffect(() => {
    const readPending = () => {
      setPendingApplications(
        getLoadingDocuments()
          .filter(doc => doc.type === 'Application')
          .map(doc => doc.id)
      );
    };

    readPending();
    window.addEventListener('loading-docs-updated', readPending);
    return () => window.removeEventListener('loading-docs-updated', readPending);
  }, []);

  // Listen for job completion events
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      const resourceTypeName = docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        // Don't show notification here - Main.tsx handles it to avoid duplicates

        // Refresh session_applications from backend after successful load, waiting for
        // this application to actually appear rather than reading once and hoping.
        //
        // The spinner is cleared *after* the list has been read back, not before. Doing
        // it first left a second or so in which the application was neither loading nor
        // listed — on the empty state that reads as the add having failed.
        (async () => {
          try {
            await refreshSessionApplications(docType === 'application' ? docId : undefined);
          } finally {
            removeLoadingDocument(docId, resourceTypeName);
            window.dispatchEvent(new Event('storage-update'));
          }
        })();
      } else {
        // Don't show error notifications here - Main.tsx handles all notifications
        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context on failure
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [addNotification, refreshSessionApplications]);

  // Listen for storage updates to refresh the application list
  useEffect(() => {
    const handleStorageUpdate = async () => {
      // Fetch fresh session applications from backend when storage updates
      try {
        const freshApplications = await patentApi.getSessionApplications(true);
        setSessionApplications(freshApplications);
      } catch (error) {
        console.error('Failed to refresh session applications on storage update:', error);
      }
    };

    window.addEventListener('storage-update', handleStorageUpdate);

    return () => {
      window.removeEventListener('storage-update', handleStorageUpdate);
    };
  }, [setSessionApplications]);

  const pollJobStatus = (jobId: string, docType: string, docId: string) => {
    // Use global polling store that persists across navigation
    startPolling(jobId, docType, docId);
  };

  /**
   * Add one application to the user's active documents.
   *
   * `quiet` suppresses the per-application notifications, for the bulk add — twenty
   * applications firing three toasts each buries the screen, and the caller reports a
   * summary instead. The return value is what lets it: the caller cannot otherwise tell
   * a duplicate from a failure from a fetch that started.
   */
  const addToActiveDocuments = async (
    applicationNumber: string,
    options: { quiet?: boolean } = {}
  ): Promise<'added' | 'duplicate' | 'failed'> => {
    const { quiet = false } = options;
    try {
      // Clean the application number - remove prefixes
      let cleanedId = applicationNumber
        .replace(/^(patent-|publication-|application-)/, '')
        .replace(/,/g, '')
        .replace(/\//g, '')
        .replace(/^US/i, '')
        .replace(/[A-Z]\d*$/i, '');

      // Check if already in context
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
      const currentApps = Array.isArray(context.current_applications) ? context.current_applications : [];

      if (currentApps.includes(cleanedId)) {
        if (!quiet) {
          addNotification({
            type: 'info',
            title: 'Already Added',
            message: `${cleanedId} is already in your active documents.`,
            duration: 3000,
          });
        }
        return 'duplicate';
      }

      // Call the API to fetch and load the application
      const response = await documentApi.fetchAndLoadDocument('application', cleanedId);

      if (response?.job_id) {
        // Add to session context immediately
        context.current_applications = [...currentApps, cleanedId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        // No "processing…" toast. The application appears in the list straight away as
        // a row with a spinner, and on the empty state as a panel naming what is being
        // fetched — both of which say more than a toast, sit where the result will
        // appear, and cannot outlive the work the way a timed toast did.
        addLoadingDocument(cleanedId, 'Application', cleanedId);

        // Start polling for job status
        pollJobStatus(response.job_id, 'application', cleanedId);
        return 'added';
      }

      return 'failed';
    } catch (error) {
      console.error('Failed to add to active documents:', error);
      if (!quiet) {
        addNotification({
          type: 'error',
          title: 'Failed to Add',
          message: 'Failed to add application to active documents. Please try again.',
          duration: 5000,
        });
      }
      return 'failed';
    }
  };

  const handleAddNewApplication = async () => {
    if (!newApplicationNumber.trim()) {
      addNotification({
        type: 'error',
        title: 'Invalid Input',
        message: 'Please enter an application number.',
        duration: 3000,
      });
      return;
    }

    await addToActiveDocuments(newApplicationNumber);
    setNewApplicationNumber('');
    setShowAddInput(false);
  };

  /**
   * Split a pasted list into application numbers.
   *
   * Split on whitespace, newlines and semicolons — but never on commas, because a
   * comma is how an application number is routinely written (17,123,456) and splitting
   * on it turns one number into three. `addToActiveDocuments` strips the commas, along
   * with slashes and a US prefix, so a trailing comma from a comma-separated list is
   * dealt with there.
   */
  const parseApplicationNumbers = (text: string): string[] => {
    const seen = new Set<string>();
    return text
      .split(/[\s;]+/)
      .map(entry => entry.trim().replace(/,$/, ''))
      .filter(entry => {
        if (!entry || !/\d/.test(entry)) return false;
        // Keyed the way `addToActiveDocuments` cleans, so "US17123456" and "17/123,456"
        // are recognised as the one application rather than counted twice.
        const key = entry.replace(/[,/\s]/g, '').replace(/^US/i, '').toUpperCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const handleBulkAddApplications = async () => {
    const numbers = parseApplicationNumbers(bulkApplicationNumbers);

    if (numbers.length === 0) {
      addNotification({
        type: 'error',
        title: 'Nothing to Add',
        message: 'Enter one or more application numbers, one per line.',
        duration: 3000,
      });
      return;
    }

    setBulkProgress({ done: 0, total: numbers.length });

    // One at a time. Each one is a USPTO fetch queued as a job, and firing twenty at
    // once buys nothing — the work is on the backend either way — while making a
    // failure impossible to attribute.
    const counts = { added: 0, duplicate: 0, failed: 0 };
    for (const [index, number] of numbers.entries()) {
      const result = await addToActiveDocuments(number, { quiet: true });
      counts[result] += 1;
      setBulkProgress({ done: index + 1, total: numbers.length });
    }

    setBulkProgress(null);
    setBulkApplicationNumbers('');
    setShowBulkInput(false);

    const parts = [
      counts.added > 0 ? `${counts.added} added` : '',
      counts.duplicate > 0 ? `${counts.duplicate} already there` : '',
      counts.failed > 0 ? `${counts.failed} failed` : '',
    ].filter(Boolean);

    addNotification({
      type: counts.failed > 0 ? 'error' : 'success',
      title: counts.added > 0 ? 'Applications Loading' : 'Nothing Added',
      message:
        `${parts.join(', ')}. ` +
        (counts.added > 0
          ? 'They appear here as the USPTO data arrives.'
          : 'Check the numbers and try again.'),
      duration: counts.failed > 0 ? 8000 : 5000,
    });
  };

  const handleRefreshApplication = async (applicationNumber: string) => {
    // Prevent multiple refreshes of the same application
    if (refreshingApps.has(applicationNumber)) return;

    setRefreshingApps(prev => new Set([...prev, applicationNumber]));

    try {
      addNotification({
        type: 'info',
        title: 'Refreshing Application',
        message: `Fetching latest data for ${applicationNumber} from USPTO...`,
        duration: 3000,
      });

      // Refresh the specific application from backend
      await patentApi.refreshApplication(applicationNumber);

      // Clear localStorage cache for this application
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes(`pros_info-${applicationNumber}`) ||
          key.includes(`pros_doc-${applicationNumber}`) ||
          key.includes(`app_info-${applicationNumber}`)
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // After refresh, reload session applications to get updated data (force override cache)
      const freshApplications = await patentApi.getSessionApplications(true);
      setSessionApplications(freshApplications);

      // Update session_applications in localStorage if the store doesn't do it automatically
      localStorage.setItem('session_applications', JSON.stringify(freshApplications));

      // Trigger storage update event to refresh other components
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Refresh Complete',
        message: `${applicationNumber} has been updated with latest USPTO data.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to refresh application:', error);
      addNotification({
        type: 'error',
        title: 'Refresh Failed',
        message: `Failed to refresh ${applicationNumber}. Please try again.`,
        duration: 5000,
      });
    } finally {
      setRefreshingApps(prev => {
        const newSet = new Set(prev);
        newSet.delete(applicationNumber);
        return newSet;
      });
    }
  };

  const handleRefreshAllApplications = async () => {
    if (refreshingAll) return;

    setRefreshingAll(true);

    try {
      addNotification({
        type: 'info',
        title: 'Refreshing Applications',
        message: 'Fetching latest application list from backend...',
        duration: 3000,
      });

      // Fetch fresh session applications from backend (force override cache)
      const freshApplications = await patentApi.getSessionApplications(true);
      setSessionApplications(freshApplications);

      // Update session_applications in localStorage
      localStorage.setItem('session_applications', JSON.stringify(freshApplications));

      // Trigger storage update event to refresh other components
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Refresh Complete',
        message: `Application list updated with ${freshApplications?.length || 0} applications.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to refresh applications:', error);
      addNotification({
        type: 'error',
        title: 'Refresh Failed',
        message: 'Failed to refresh application list. Please try again.',
        duration: 5000,
      });
    } finally {
      setRefreshingAll(false);
    }
  };

  /**
   * Take an application out of the session context.
   *
   * The context is what the chat reasons over and what the Documents page lists, so a
   * deleted application has to leave it — otherwise it is gone from this page and still
   * present everywhere it actually gets used. Written the way the Documents page writes
   * it, including the singular `current_application` pointer, which is easy to miss and
   * keeps a deleted application as "the" application of the session.
   */
  const removeApplicationFromSessionContext = (applicationNumber: string) => {
    try {
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== 'undefined' ? JSON.parse(stored) : {};

      const bare = applicationNumber.replace(/[,/\s]/g, '').replace(/^US/i, '');
      const prefixed = `application-${bare}`;
      const matches = (value: string) =>
        value === bare || value === prefixed || value === applicationNumber;

      if (Array.isArray(context.current_applications)) {
        context.current_applications = context.current_applications.filter(
          (id: string) => !matches(id)
        );
      }
      if (Array.isArray(context.resource_ids)) {
        context.resource_ids = context.resource_ids.filter((id: string) => !matches(id));
      }
      if (Array.isArray(context.new_resource_ids)) {
        context.new_resource_ids = context.new_resource_ids.filter(
          (id: string) => !matches(id)
        );
      }
      if (Array.isArray(context.extracted_resources)) {
        context.extracted_resources = context.extracted_resources.filter((resource: any) => {
          const id = resource?.resource_id || resource?.id || resource;
          return typeof id === 'string' ? !matches(id) : true;
        });
      }
      if (typeof context.current_application === 'string' && matches(context.current_application)) {
        context.current_application = null;
      }

      localStorage.setItem('session_context', JSON.stringify(context));

      // The Documents page reads this store, so it updates without a reload.
      setSessionDocuments(
        (useSessionStore.getState().sessionDocuments || []).filter((doc: any) => {
          const id = doc?.resource_id;
          return typeof id === 'string' ? !matches(id) : true;
        })
      );
    } catch (error) {
      console.error('Failed to update session context after deleting application:', error);
    }
  };

  const handleDeleteApplication = async () => {
    if (!applicationToDelete || isDeletingApp) return;

    const applicationNumber = applicationToDelete.number;
    setIsDeletingApp(true);

    try {
      await patentApi.deleteApplication(applicationNumber);

      // Also drop it from the user's active documents. Deleting the application while
      // leaving it in the session means the chat still reasons over it and the Documents
      // page still lists it — the same delete the Documents page performs, so the two
      // pages agree about what "deleted" means.
      try {
        await documentApi.deleteDocument('application', applicationNumber);
      } catch (docError) {
        // An application that was never opened as a document has no session record to
        // delete. That is not a failed delete, so it does not stop the rest.
        console.warn('No session document to delete for', applicationNumber, docError);
      }

      removeApplicationFromSessionContext(applicationNumber);

      // The caches that hold this application, so a deleted one does not come back from
      // localStorage the next time something reads it.
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes(`pros_info-${applicationNumber}`) ||
          key.includes(`pros_doc-${applicationNumber}`) ||
          key.includes(`app_info-${applicationNumber}`)
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Read the list back from the backend rather than filtering locally, so what is on
      // screen is what is stored.
      const freshApplications = await patentApi.getSessionApplications(true);
      setSessionApplications(freshApplications);
      localStorage.setItem('session_applications', JSON.stringify(freshApplications));
      window.dispatchEvent(new Event('storage-update'));

      // The detail pane is showing the application that no longer exists.
      setSelectedResource(current => (current?.number === applicationNumber ? null : current));
      setApplicationToDelete(null);

      addNotification({
        type: 'success',
        title: 'Application Removed',
        message: `${applicationNumber} has been removed from your applications and active documents.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to delete application:', error);
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: `Failed to remove ${applicationNumber}. Please try again.`,
        duration: 5000,
      });
    } finally {
      setIsDeletingApp(false);
    }
  };

  // Create resources from sessionApplications
  const usptoResources = useMemo(() => {
    const resources: ApplicationResource[] = [];

    if (sessionApplications?.length > 0) {
      sessionApplications.forEach((sessionApp: any) => {
        const appNumber = sessionApp.application_id || sessionApp.application?.applicationNumberText;

        // Filter out null/empty applications
        const hasValidData = sessionApp.application &&
          sessionApp.application.applicationMetaData &&
          sessionApp.application.applicationMetaData.inventionTitle &&
          sessionApp.application.applicationMetaData.inventionTitle !== 'null';

        if (appNumber && hasValidData) {
          resources.push({
            id: `app-${appNumber}`,
            type: 'application',
            number: appNumber,
            data: {
              application_number: appNumber,
              patent_info: {
                applicationMetaData: sessionApp.application?.applicationMetaData || {}
              },
              pgpubDocumentMetaData: sessionApp.application?.pgpubDocumentMetaData,
              grantDocumentMetaData: sessionApp.application?.grantDocumentMetaData,
            }
          });
        }
      });
    }

    return resources;
  }, [sessionApplications]);

  // Helper functions
  const getResourceTitle = (resource: ApplicationResource) => {
    const metaData = resource.data?.patent_info?.applicationMetaData;
    return metaData?.inventionTitle || `Application ${resource.number}`;
  };

  const getResourceStatus = (resource: ApplicationResource) => {
    const metaData = resource.data?.patent_info?.applicationMetaData;
    const statusText = metaData?.applicationStatusDescriptionText?.toLowerCase() || '';

    // Check for abandoned status
    if (statusText.includes('abandon')) {
      return 'Abandoned';
    }

    // Check for expired status
    if (statusText.includes('expired')) {
      return 'Expired';
    }

    // Check for granted/patented status
    if (metaData?.grantDate || metaData?.patentNumber || statusText.includes('patent')) {
      return 'Granted';
    }

    // Default to pending
    return 'Pending';
  };

  // Filter resources based on search term and status
  /**
   * Drop a pending entry once its application is actually in the list.
   *
   * Self-healing for the case the job-complete event never lands — a tab closed
   * mid-fetch, a reload during one — which would otherwise leave a spinner in the
   * sidebar for good, since the entry lives in localStorage and outlives the page.
   */
  useEffect(() => {
    if (pendingApplications.length === 0) return;

    const digits = (value: string) => value.replace(/\D/g, '');
    const listed = new Set(usptoResources.map(resource => digits(resource.number)));
    const landed = pendingApplications.filter(number => listed.has(digits(number)));

    landed.forEach(number => removeLoadingDocument(number, 'Application'));
  }, [pendingApplications, usptoResources]);

  const filteredResources = useMemo(() => {
    if (!usptoResources.length) return [];

    return usptoResources.filter(resource => {
      const data = resource.data;
      const metaData = data?.patent_info?.applicationMetaData;

      // Search filter
      const searchTerm = searchQuery.toLowerCase().trim();
      if (searchTerm) {
        const searchableFields = [
          resource.number,
          metaData?.inventionTitle,
          metaData?.firstInventorName,
          metaData?.assigneeName,
          metaData?.docketNumber,
          metaData?.patentNumber,
          metaData?.earliestPublicationNumber,
        ].filter(Boolean).map(field => String(field).toLowerCase());

        if (!searchableFields.some(field => field.includes(searchTerm))) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== 'all') {
        const resourceStatus = getResourceStatus(resource).toLowerCase();
        if (resourceStatus !== filterStatus) return false;
      }

      return true;
    });
  }, [usptoResources, searchQuery, filterStatus]);

  // Auto-select first resource
  useEffect(() => {
    if (filteredResources.length > 0 && !selectedResource) {
      setSelectedResource(filteredResources[0]);
    }
  }, [filteredResources, selectedResource]);

  /**
   * A link straight to one application: /?view=uspto_data&app=16123456 (the docket's
   * USPTO scan links here). Selects it when it is in the list; otherwise adds it, and
   * selects it once the fetch lands — so the link works for an application the user
   * has never opened.
   */
  const linkedApp = (searchParams.get('app') || '').replace(/\D/g, '');
  const linkedHandled = useRef('');
  useEffect(() => {
    if (!linkedApp || linkedHandled.current === `${linkedApp}:selected`) return;
    const match = usptoResources.find(resource => resource.number.replace(/\D/g, '') === linkedApp);
    if (match) {
      setSearchQuery('');
      setFilterStatus('all');
      setSelectedResource(match);
      linkedHandled.current = `${linkedApp}:selected`;
      return;
    }
    if (isLoading || linkedHandled.current === `${linkedApp}:adding`) return;
    linkedHandled.current = `${linkedApp}:adding`;
    addToActiveDocuments(linkedApp, { quiet: true });
  }, [linkedApp, usptoResources, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const highlightText = (text: string | null | undefined, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 text-gray-900 px-1 rounded">
          {part}
        </span>
      ) : part
    );
  };

  /**
   * The bulk paste box. Rendered in the sidebar and in the empty state, so it is written
   * once — the empty state is where it matters most (a new user has nothing to look at
   * and a list of matters to load), and the sidebar is where it is wanted afterwards.
   */
  /**
   * The single-number add box, with the way across to the bulk one.
   *
   * Shared by the sidebar and the empty state. The link matters: someone with a list of
   * twenty numbers clicks "Add Application" like everybody else, and finding out only
   * then that there is a bulk box — rather than adding twenty one at a time — is the
   * whole point of putting it here.
   */
  const renderSingleAddInput = ({ compact }: { compact?: boolean } = {}) => (
    <div className="space-y-2">
      <div className={compact ? 'space-y-2' : 'flex gap-2'}>
        <Input
          type="text"
          placeholder="Application number (e.g., 17123456)"
          value={newApplicationNumber}
          onChange={(e) => setNewApplicationNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddNewApplication();
            } else if (e.key === 'Escape') {
              setShowAddInput(false);
              setNewApplicationNumber('');
            }
          }}
          className={compact ? 'w-full' : 'flex-1'}
          autoFocus
        />
        {compact ? (
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddNewApplication} className="flex-1">
              Add
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowAddInput(false);
                setNewApplicationNumber('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button onClick={handleAddNewApplication} className="gap-2">
            <Plus className="w-4 h-4" />
            Add
          </Button>
        )}
      </div>
      <div className={compact ? '' : 'text-center'}>
        <button
          type="button"
          onClick={() => {
            setShowAddInput(false);
            setShowBulkInput(true);
          }}
          className="text-sm text-blue-600 hover:underline"
        >
          Add several at once
        </button>
      </div>
    </div>
  );

  const renderBulkAddInput = () => (
    <div className="space-y-2">
      <Textarea
        placeholder={'One application number per line\n17123456\n17/234,567'}
        value={bulkApplicationNumbers}
        onChange={(e) => setBulkApplicationNumbers(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !bulkProgress) {
            setShowBulkInput(false);
            setBulkApplicationNumbers('');
          }
        }}
        rows={5}
        className="w-full text-sm"
        disabled={!!bulkProgress}
        autoFocus
      />
      <p className="text-xs text-gray-500">
        Separated by new lines, spaces or semicolons. Slashes and commas inside a number
        are fine.
      </p>
      {bulkProgress ? (
        <p className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          Adding {bulkProgress.done} of {bulkProgress.total}…
        </p>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" onClick={handleBulkAddApplications} className="flex-1">
            Add {parseApplicationNumbers(bulkApplicationNumbers).length || ''} applications
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setShowBulkInput(false);
              setBulkApplicationNumbers('');
            }}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );

  // Show loading state while fetching session applications
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50">
        <Loader2 className="w-10 h-10 text-blue-600 mb-3 animate-spin" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading USPTO Data...</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          Fetching your session applications
        </p>
      </div>
    );
  }

  // Nothing here yet. The old empty state sent the user to the chat to add an
  // application, which was both a detour and out of date — this page adds them itself,
  // one at a time or a pasted list at a time, so it offers that rather than describing it.
  if (usptoResources.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 px-6">
        <Database className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No applications yet</h3>
        <p className="text-sm text-gray-500 text-center max-w-md mb-5">
          Add an application by its number and its USPTO data — status, prosecution
          history, claims and cited art — is fetched and kept here.
        </p>

        <div className="w-full max-w-md space-y-3">
          {/* The first application is being fetched. Without this the page still says
              "no applications yet" while the work is under way, and the only honest
              reading of that is that the click did nothing. */}
          {pendingApplications.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="flex items-center gap-2 text-sm font-medium text-blue-900">
                <Loader2 className="w-4 h-4 animate-spin" />
                Fetching {pendingApplications.length} application
                {pendingApplications.length !== 1 ? 's' : ''} from the USPTO…
              </p>
              <p className="mt-1 text-xs text-blue-800">
                {pendingApplications.join(', ')}
              </p>
              <p className="mt-2 text-xs text-blue-700">
                This takes up to a minute each. They appear here as they arrive — you can
                leave this page and come back.
              </p>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            {showBulkInput ? renderBulkAddInput() : renderSingleAddInput()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Sidebar - Resource List */}
      <div className={`flex-shrink-0 h-full border-r border-gray-200 bg-white overflow-y-auto transition-all duration-300 ${
        isCollapsed ? 'w-12' : 'w-80'
      }`}>
        {/* Header */}
        <div className="border-b border-gray-200 px-4 py-3 bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between mb-3">
            {!isCollapsed && (
              <h2 className="text-lg font-semibold text-gray-900">USPTO Applications</h2>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>

          {!isCollapsed && (
            <>
              {/* Add Application Button */}
              {!showAddInput && !showBulkInput && (
                <div className="mb-3 flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowAddInput(true)}
                    className="gap-2 flex-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Application
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkInput(true)}
                    className="gap-2"
                    title="Add several applications at once"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshAllApplications}
                    disabled={refreshingAll}
                    className="gap-2"
                    title="Refresh application list from backend"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshingAll ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              )}

              {/* Bulk Add Input */}
              {showBulkInput && <div className="mb-3">{renderBulkAddInput()}</div>}

              {/* Add Application Input */}
              {showAddInput && (
                <div className="mb-3">{renderSingleAddInput({ compact: true })}</div>
              )}

              <p className="text-sm text-gray-500 mb-3">
                {searchQuery.trim() || filterStatus !== 'all'
                  ? `${filteredResources.length} of ${usptoResources.length} applications`
                  : `${usptoResources.length} application${usptoResources.length !== 1 ? 's' : ''}`
                }
              </p>

              {/* Search Input */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search applications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Applications</SelectItem>
                  <SelectItem value="granted">Granted</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="abandoned">Abandoned</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>

              {/* Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={usptoResources.length === 0}
                className="gap-2 w-full mt-3"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export to Excel
              </Button>
            </>
          )}
        </div>

        {/* Application List */}
        {!isCollapsed && (
          <div className="p-2">
            {/* Applications still being fetched, above the list and out of the way of the
                search and filter — they are not in the list yet, so filtering them would
                mean hiding the very thing the user just asked for. */}
            {pendingApplications.map(number => (
              <div
                key={`pending-${number}`}
                className="w-full p-3 mb-2 rounded-lg border border-blue-200 bg-blue-50"
              >
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">{number}</span>
                </div>
                <p className="mt-1 text-xs text-blue-700">
                  Fetching from the USPTO…
                </p>
              </div>
            ))}

            {filteredResources.length > 0 ? (
              filteredResources.map((resource) => {
                const metaData = resource.data?.patent_info?.applicationMetaData;
                const status = getResourceStatus(resource);

                return (
                  <div
                    key={resource.id}
                    className={`w-full p-3 mb-2 rounded-lg border transition-colors ${
                      selectedResource?.id === resource.id
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => setSelectedResource(resource)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <Badge
                          variant="outline"
                          className={
                            status === 'Granted'
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : status === 'Abandoned'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : status === 'Expired'
                              ? 'bg-orange-100 text-orange-800 border-orange-200'
                              : 'bg-blue-100 text-blue-800 border-blue-200'
                          }
                        >
                          {status}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefreshApplication(resource.number);
                          }}
                          disabled={refreshingApps.has(resource.number)}
                          title="Refresh application data from USPTO"
                        >
                          <RefreshCw className={`w-3 h-3 ${refreshingApps.has(resource.number) ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>

                      <h3 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">
                        {highlightText(getResourceTitle(resource), searchQuery)}
                      </h3>

                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">App. No.</span> {highlightText(resource.number, searchQuery)}
                      </p>

                      {/* Show additional searchable info when searching */}
                      {searchQuery && (
                        <div className="text-xs text-gray-600 space-y-1 mt-2">
                          {metaData?.firstInventorName && metaData.firstInventorName !== 'Loading...' && (
                            <div className="truncate">
                              <span className="font-medium">Inventor:</span> {highlightText(metaData.firstInventorName, searchQuery)}
                            </div>
                          )}
                          {metaData?.docketNumber && (
                            <div className="truncate">
                              <span className="font-medium">Docket:</span> {highlightText(metaData.docketNumber, searchQuery)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-gray-600">
                {searchQuery || filterStatus !== 'all' ? (
                  <div>
                    <p className="text-sm mb-2">No applications match your filters</p>
                    <p className="text-xs opacity-75">Try adjusting your search or filter</p>
                  </div>
                ) : (
                  <p className="text-sm">No applications available</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 h-full overflow-y-auto bg-gray-50">
        {selectedResource ? (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Badge
                  variant="outline"
                  className={
                    getResourceStatus(selectedResource) === 'Granted'
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : getResourceStatus(selectedResource) === 'Abandoned'
                      ? 'bg-red-100 text-red-800 border-red-200'
                      : getResourceStatus(selectedResource) === 'Expired'
                      ? 'bg-orange-100 text-orange-800 border-orange-200'
                      : 'bg-blue-100 text-blue-800 border-blue-200'
                  }
                >
                  US APPLICATION
                </Badge>
                <h1 className="text-2xl font-bold text-gray-900">
                  {getResourceTitle(selectedResource)}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <p className="">
                  <span className="font-semibold">App. No.</span> {selectedResource.number}
                </p>
              {selectedResource.data?.patent_info?.applicationMetaData?.docketNumber && (
                <div className="truncate">
                  <span className="font-medium">Docket No.</span> {highlightText(selectedResource.data?.patent_info?.applicationMetaData?.docketNumber, searchQuery)}
                </div>
              )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRefreshApplication(selectedResource.number)}
                  disabled={refreshingApps.has(selectedResource.number)}
                  className="h-7 text-xs gap-1"
                  title="Refresh application data from USPTO"
                >
                  <RefreshCw className={`w-3 h-3 ${refreshingApps.has(selectedResource.number) ? 'animate-spin' : ''}`} />
                  {refreshingApps.has(selectedResource.number) ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setApplicationToDelete(selectedResource)}
                  className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  title="Remove this application from your applications"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addToActiveDocuments(selectedResource.number)}
                  className="h-7 text-xs gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add to Active Documents
                </Button>
              </div>
            </div>

            <USApplicationDisplay
              applicationData={selectedResource.data}
              onDocumentClick={onDocumentSelect}
              onProsecutionDocClick={onDocumentSelect}
            />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select an Application</h3>
              <p className="text-sm text-gray-500">
                Choose an application from the sidebar to view details
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Removing an application is not undoable from here — it has to be added and
          fetched again — so it asks first. */}
      <AlertDialog
        open={!!applicationToDelete}
        onOpenChange={open => {
          if (!open && !isDeletingApp) setApplicationToDelete(null);
        }}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {applicationToDelete?.number} from your applications? It is taken out
              of this list and out of your active documents, so the chat and the Documents
              page no longer hold it. The application itself is not deleted from the USPTO
              or from Patent Loop — you can add the number again to get it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingApp}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={event => {
                // The dialog closes itself on action; keep it open while the request runs
                // so the spinner is visible and a second click cannot land.
                event.preventDefault();
                handleDeleteApplication();
              }}
              disabled={isDeletingApp}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingApp ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Modal */}
      <ExportModal
        open={showExportModal}
        onOpenChange={setShowExportModal}
        applications={usptoResources.map(r => ({
          application_id: r.id,
          application_number: r.number,
          application_data: r.data.patent_info
        }))}
      />
    </div>
  );
}
